import { decamelizeKeys } from "humps";
import { TemplateKey } from "PFApp/constants/templates";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { useTemplate, useTemplateFind } from "PFApp/hooks";
import NotFoundComponent from "PFComponents/not_found/not_found";
import { PageWrapper } from "PFComponents/page_wrapper";
import { useActivity } from "PFCore/hooks/queries/activity";
import {
  useEngagementBlueprint,
  useEngagementBlueprintActivities
} from "PFCore/hooks/queries/engagement_blueprints";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router";

import { ApplyEngagementBlueprintPage } from "./apply_engagement_blueprint_page";

export const ApplyEngagementBlueprintPageController = () => {
  const { t } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });
  const { id: engagementId, engagementBlueprintId } = useParams<{
    id: string;
    engagementBlueprintId: string;
  }>();
  const findTemplate = useTemplateFind();
  const engagementTemplate = useTemplate(TemplateKey.Engagement);

  const {
    data: engagementBlueprint,
    isLoading: isLoadingBlueprint,
    isError: isErrorBlueprint
  } = useEngagementBlueprint(Number(engagementBlueprintId));

  const {
    data: blueprintActivities,
    isLoading: isLoadingBlueprintActivities,
    isError: isErrorBlueprintActivities
  } = useEngagementBlueprintActivities(
    Number(engagementBlueprintId),
    {
      page: 1,
      perPage: PER_PAGE_UNPAGINATED
    },
    { select: (data) => decamelizeKeys(data) as any }
  );

  const {
    data: activity,
    isLoading: isActivityLoading,
    isError: isActivityError
  } = useActivity(Number(engagementId));

  const isLoading = isLoadingBlueprint || isActivityLoading || isLoadingBlueprintActivities;
  const isError = isErrorBlueprint || isActivityError || isErrorBlueprintActivities;
  const isAllowedToCreateRoles = !!findTemplate({
    key: TemplateKey.Role,
    create_permitted: true,
    hidden: false
  });
  const isClosed = activity?.state === "complete";
  const isEngagement = activity?.template_id === engagementTemplate?.id;
  const canAddRoles = !isClosed && isEngagement && isAllowedToCreateRoles;

  if (!isLoading && !isError && !canAddRoles) {
    return <Navigate to={`/activities/${engagementId}`} replace />;
  }

  return (
    <PageWrapper
      loading={isLoading}
      errors={isError ? [t("loadError")] : undefined}
      errorComponent={
        <NotFoundComponent
          message={t("notFoundMessage")}
          buttonMessage={t("notFoundButton")}
          buttonUrl={`/activities/${engagementId}`}
        />
      }
    >
      <ApplyEngagementBlueprintPage
        parentActivity={activity!}
        engagementBlueprint={engagementBlueprint!}
        engagementBlueprintActivities={blueprintActivities?.entries || []}
      />
    </PageWrapper>
  );
};
